import React from 'react';

import { INutritionInfo } from '@rbi-ctg/menu';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';

import { TableData, TableRow } from '../styled/table';
import { FALLBACK_NUTRITION_VALUE } from '../utils';

import { SanityNutritionAdditionalItem, SanityNutritionItem } from './types';

type NutritionItemProps = {
  nutritionItems: { items: SanityNutritionItem[] }[];
  nutritionInfo: INutritionInfo;
  roundNutritionValue: (val: number) => number;
};

interface NutritionTableRow {
  nutritionItemKey: string | null;
  nutritionItemName: string | null;
  nutritionPrimaryValue: string | null;
  nutritionAdditionalValue: string | null;
  nutritionUnitPrimaryValue: string | null;
  nutritionUnitAdditionalValue: string | null;
}

function separateNutritionItems(nutritionItems: { items: SanityNutritionItem[] }[]) {
  const perServingNutritionItems: SanityNutritionItem[] = [];
  const perUnitNutritionItems: SanityNutritionItem[] = [];

  nutritionItems?.forEach(nutritionItem => {
    nutritionItem?.items?.forEach(item => {
      if (item?.nutritionKey?.includes('Per100')) {
        perUnitNutritionItems.push(item);
      } else {
        perServingNutritionItems.push(item);
      }
    });
  });

  return {
    perServingNutritionItems,
    perUnitNutritionItems,
  };
}

function getAdditionalNutritionValue(
  additionalItem: SanityNutritionAdditionalItem,
  nutritionInfo: INutritionInfo,
  roundNutritionValue: (val: number) => number
) {
  if (!additionalItem?.nutritionKey) {
    return null;
  }

  const additionalItemUnit = additionalItem.unit?.locale ?? '';

  const additionalItemValue =
    nutritionInfo[additionalItem.nutritionKey as keyof INutritionInfo] || 0;
  const shouldOmitAdditionalUnit = additionalItem.omitUnitFromValue;

  const roundedAdditionalItemValue = roundNutritionValue(additionalItemValue);

  const isValidRoundedAdditionalItemValue =
    roundedAdditionalItemValue !== undefined &&
    roundedAdditionalItemValue !== null &&
    roundedAdditionalItemValue !== 0;

  let result = '';
  if (isValidRoundedAdditionalItemValue) {
    if (shouldOmitAdditionalUnit) {
      result = `${roundedAdditionalItemValue}`;
    } else {
      result = `${roundedAdditionalItemValue}${additionalItemUnit}`;
    }
  } else {
    result = `${FALLBACK_NUTRITION_VALUE}${additionalItemUnit}`;
  }

  return result;
}

export const NutritionItem: React.FC<NutritionItemProps> = ({
  nutritionInfo,
  nutritionItems,
  roundNutritionValue,
  ...props
}) => {
  const enableFormatNutritionCommaSeparator = useFlag(
    LaunchDarklyFlag.ENABLE_FORMAT_NUTRITION_COMMA_SEPARATOR
  );
  const displayPer100ValuesFirst =
    nutritionItems?.length > 0 &&
    nutritionItems[0]?.items.every(item => item.nutritionKey?.includes('Per100'));

  const { perServingNutritionItems, perUnitNutritionItems } =
    separateNutritionItems(nutritionItems);

  let nutritionTableRows: NutritionTableRow[] = [];

  const formatNutritionValue = (value: string | null): string | null =>
    enableFormatNutritionCommaSeparator ? (value?.replace('.', ',') ?? null) : value;

  perServingNutritionItems?.forEach(nutritionItem => {
    const nutritionItemKey = nutritionItem?.nutritionKey || '';
    const unitNutritionItem = perUnitNutritionItems?.find(
      item => item.nutritionKey?.replace('Per100', '') === nutritionItemKey
    );

    const primaryItemValue = nutritionInfo[nutritionItemKey as keyof INutritionInfo] ?? 0;
    const primaryItemUnit = nutritionItem.unit?.locale ?? '';
    const nutritionItemName: string = nutritionItem.name?.locale ?? '';
    const shouldOmitPrimaryUnit = nutritionItem.omitUnitFromValue ?? '';

    const perUnitPrimaryItemValue =
      nutritionInfo[unitNutritionItem?.nutritionKey as keyof INutritionInfo] ?? 0;

    const perUnitPrimaryItemUnit = unitNutritionItem?.unit?.locale;
    const perUnitShouldOmitPrimaryUnit = unitNutritionItem?.omitUnitFromValue;

    const roundedPrimaryItemValue = roundNutritionValue(primaryItemValue);
    const roundedUnitPrimaryItemValue = roundNutritionValue(perUnitPrimaryItemValue);

    const isValidRoundedPrimaryItemValue =
      roundedPrimaryItemValue !== undefined &&
      roundedPrimaryItemValue !== null &&
      roundedPrimaryItemValue !== 0;

    const isValidRoundedUnitPrimaryItemValue =
      roundedUnitPrimaryItemValue !== undefined &&
      roundedUnitPrimaryItemValue !== null &&
      roundedUnitPrimaryItemValue !== 0;

    let nutritionPrimaryValue = null;
    if (isValidRoundedPrimaryItemValue) {
      nutritionPrimaryValue = shouldOmitPrimaryUnit
        ? `${roundedPrimaryItemValue}`
        : `${roundedPrimaryItemValue}${primaryItemUnit}`;
    } else {
      nutritionPrimaryValue = `${FALLBACK_NUTRITION_VALUE}${primaryItemUnit}`;
    }

    let nutritionUnitPrimaryValue;

    if (nutritionItemKey === 'weight') {
      nutritionUnitPrimaryValue = '100g';
    } else if (isValidRoundedUnitPrimaryItemValue) {
      nutritionUnitPrimaryValue = perUnitShouldOmitPrimaryUnit
        ? `${roundedUnitPrimaryItemValue}`
        : `${roundedUnitPrimaryItemValue}${perUnitPrimaryItemUnit}`;
    } else {
      nutritionUnitPrimaryValue = `${FALLBACK_NUTRITION_VALUE}${perUnitPrimaryItemUnit}`;
    }

    const firstAdditionalItem = nutritionItem.additionalItems?.[0] as SanityNutritionAdditionalItem;
    const firstUnitAdditionalItem = unitNutritionItem
      ?.additionalItems?.[0] as SanityNutritionAdditionalItem;

    let nutritionAdditionalValue = null;
    let nutritionUnitAdditionalValue = null;

    nutritionAdditionalValue = getAdditionalNutritionValue(
      firstAdditionalItem,
      nutritionInfo,
      roundNutritionValue
    );
    nutritionUnitAdditionalValue = getAdditionalNutritionValue(
      firstUnitAdditionalItem,
      nutritionInfo,
      roundNutritionValue
    );

    nutritionTableRows.push({
      nutritionItemKey,
      nutritionItemName,
      nutritionPrimaryValue: formatNutritionValue(nutritionPrimaryValue),
      nutritionAdditionalValue: formatNutritionValue(nutritionAdditionalValue),
      nutritionUnitPrimaryValue: formatNutritionValue(nutritionUnitPrimaryValue),
      nutritionUnitAdditionalValue: formatNutritionValue(nutritionUnitAdditionalValue),
    });
  });

  const nutritionItemsList = nutritionTableRows?.map((row, index) => {
    const nutritionBlock = (
      <TableData>
        <span data-testid="primary-item-nutrition-value">{row?.nutritionPrimaryValue}</span>
        {row?.nutritionAdditionalValue && (
          <span data-testid="additional-item-nutrition-value">
            {' / '}
            {row?.nutritionAdditionalValue}
          </span>
        )}
      </TableData>
    );

    const per100NutritionBlock = (
      <TableData>
        <span data-testid="primary-item-nutrition-value-100g">
          {row?.nutritionUnitPrimaryValue}
        </span>
        {row?.nutritionUnitAdditionalValue && (
          <span data-testid="additional-item-nutrition-value-100g">
            {' / '}
            {row?.nutritionUnitAdditionalValue}
          </span>
        )}
      </TableData>
    );

    return (
      <TableRow key={`${row?.nutritionItemKey}-${index}`} {...props}>
        <TableData data-testid="nutrition-item-name" scope="row">
          {row?.nutritionItemName}
        </TableData>

        {displayPer100ValuesFirst ? (
          <>
            {per100NutritionBlock}
            {nutritionBlock}
          </>
        ) : (
          <>
            {nutritionBlock}
            {per100NutritionBlock}
          </>
        )}
      </TableRow>
    );
  });

  return <>{nutritionItemsList}</>;
};
